import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { App, Button, Card, Flex, Form, Input, Result, Spin, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { invitationAccept, invitationVerifyToken } from '../../../libs/ohat-api';
import { getApiErrorMessage } from '../../../utils/apiError';

type VerifyState =
  | { status: 'loading' }
  | { status: 'invalid'; message: string }
  | { status: 'valid'; email: string; organizationName?: string };

export const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { t } = useTranslation();
  const { signUpCognito, signInCognito, signOutCognito } = useAuth();

  const [verify, setVerify] = useState<VerifyState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ password: string }>();

  useEffect(() => {
    if (!token) {
      setVerify({ status: 'invalid', message: t('invitation.noToken') });
      return;
    }
    invitationVerifyToken({ token })
      .then((res: any) => {
        if (res?.valid) {
          setVerify({
            status: 'valid',
            email: res.email,
            organizationName: res.organizationName,
          });
        } else {
          setVerify({
            status: 'invalid',
            message: res?.message || t('invitation.invalidOrExpired'),
          });
        }
      })
      .catch(() => {
        setVerify({ status: 'invalid', message: t('invitation.invalidOrExpired') });
      });
  }, [token, t]);

  const handleJoin = async () => {
    if (verify.status !== 'valid' || !token) return;
    const { password } = await form.validateFields();
    setSubmitting(true);
    try {
      // 同一ブラウザで別ユーザーがログイン済みだと Amplify signIn が
      // UserAlreadyAuthenticatedException で失敗するため、先に既存セッションを破棄する
      await signOutCognito();
      const signUp = await signUpCognito(verify.email, password);
      if (!signUp.success) {
        message.error(signUp.message);
        setSubmitting(false);
        return;
      }
      const sub = await signInCognito(verify.email, password);
      if (!sub) {
        message.error(t('invitation.signInFailed'));
        setSubmitting(false);
        return;
      }
      await invitationAccept({ token });
      message.success(t('invitation.joined'));
      // 新規アカウント作成＋別ユーザーへのサインイン直後のため、SPA内遷移だと
      // 認証コンテキスト（account/rawData/isAuthenticated）が中途半端な状態のまま遷移して
      // 画面が固まる。フルリロードで '/' に移動し、認証状態をクリーンに再初期化する。
      window.location.href = '/';
    } catch (e: any) {
      message.error(getApiErrorMessage(e, t('invitation.joinFailed')));
      setSubmitting(false);
    }
  };

  if (verify.status === 'loading') {
    return (
      <div style={{ padding: 50, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (verify.status === 'invalid') {
    return (
      <Result
        status="error"
        title={t('invitation.verifyFailed')}
        subTitle={verify.message}
        extra={[
          <Button key="home" type="primary" onClick={() => navigate('/signin')}>
            {t('invitation.toSignIn')}
          </Button>,
        ]}
      />
    );
  }

  return (
    <Flex vertical style={{ maxWidth: 420 }} gap={0}>
      <img
        src={`${process.env.PUBLIC_URL}/logo.svg`}
        alt="Logo"
        style={{ alignSelf: 'center', marginBottom: 25 }}
      />
      <Card title={t('invitation.title')} style={{ minWidth: 320 }}>
        <Typography.Paragraph>
          <Trans
            i18nKey="invitation.invitedBy"
            values={{ org: verify.organizationName ?? t('invitation.defaultOrg') }}
            components={{ strong: <strong /> }}
          />{' '}
          {t('invitation.lead')}
        </Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={handleJoin}>
          <Form.Item label={t('member.email')}>
            <Input value={verify.email} disabled />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('invitation.password')}
            rules={[
              { required: true, message: t('invitation.passwordRequired') },
              { min: 8, message: t('invitation.passwordMin') },
            ]}
          >
            <Input.Password placeholder={t('invitation.passwordPlaceholder')} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
            {t('invitation.submit')}
          </Button>
        </Form>
      </Card>
    </Flex>
  );
};
